module Client.Components.Graph.TipBurn

open System
open Client.Components.Graph.Recharts
open Client.Components.GraphCommon
open Feliz.Recharts
open Shared.Dto.Page

let private xDomain = xAxis.domain (domain.min, domain.max)
let private yDomain = yAxis.domain (domain.min, domain.max)

type TipBurnGraphData = {
    TemperatureAxis: float list
    HumidityAxis: float list
    Data: TipBurnData.GraphNodeDto list
    CurrentTimestamp: DateTimeOffset
}

type TipBurnGraph =
    | NoData
    | Data of TipBurnGraphData

let private createTemperatureAxisTicks (data: TipBurnData.GraphNodeDto list) =
    let airTemperatures =
        List.map (fun (graphNode: TipBurnData.GraphNodeDto) -> graphNode.AirTemperature) data

    let soilTemperatures =
        List.map (fun (graphNode: TipBurnData.GraphNodeDto) -> graphNode.SoilTemperature) data

    let minValue = min (List.min airTemperatures) (List.min soilTemperatures)
    let maxValue = max (List.max airTemperatures) (List.max soilTemperatures)

    AxisTicks.generate true 5. minValue maxValue

let createGraphData (data: TipBurnData.GraphNodeDto list) =
    let temperatureAxisTicks = createTemperatureAxisTicks data

    let humidityAxis = [ 0. .. 20. .. 100. ]

    {
        TemperatureAxis = temperatureAxisTicks
        HumidityAxis = humidityAxis
        Data = data
        CurrentTimestamp = DateTimeOffset.Now
    }

let riskGraph (graphData: TipBurnGraphData) =
    let ticks = AxisTicks.createYProperty [ 0 .. 1. .. 10. ]

    let xDomain = xAxis.domain (domain.min, domain.max)

    let yDomain = yAxis.domain (domain.min, domain.max)

    let xAxisTickInterval = xAxis.interval.preserveStartEnd

    let dataKey = (fun (p: TipBurnData.GraphNodeDto) -> p.Ticks) |> xAxis.dataKey

    Recharts.areaChart [
        areaChart.syncId "syncId1"
        areaChart.data graphData.Data
        areaChart.children [
            Recharts.xAxis [
                dataKey
                xAxis.number
                xAxis.scale.time
                xAxis.tickFormatter formatTimeTick
                xDomain
                xAxisTickInterval
            ]
            Recharts.yAxis [ ticks; yDomain ]
            Recharts.legend []
            Recharts.tooltip [ tooltip.content tooltipContentWithForecast ]
            Recharts.area [
                area.monotone
                area.dot false
                area.dataKey (fun (p: TipBurnData.GraphNodeDto) -> p.Risk)
                area.name "Risiko"
                area.stroke "#FF0000"
                area.fill "#FAA0A0"
            ]

            Recharts.cartesianGrid [ cartesianGrid.strokeDasharray [| 3; 3 |] ]
        ]
    ]
    |> fullHeightAreaGraphBox "Verlauf des Risikos"

let temperatureGraph (maybeForecastLine: Option<IReferenceLineProperty list>) (graphData: TipBurnGraphData) =
    let ticks = AxisTicks.createYProperty graphData.TemperatureAxis

    let xAxisTickInterval = xAxis.interval.preserveStartEnd

    let dataKey = (fun (p: TipBurnData.GraphNodeDto) -> p.Ticks) |> xAxis.dataKey

    Recharts.lineChart [
        lineChart.syncId "syncId1"
        lineChart.data graphData.Data
        lineChart.children [
            Recharts.xAxis [
                dataKey
                xAxis.number
                xAxis.scale.time
                xAxis.tickFormatter formatTimeTick
                xDomain
                xAxisTickInterval
            ]
            Recharts.yAxis [ ticks; yAxis.unit " °C"; yDomain ]
            Recharts.legend []
            Recharts.tooltip [ tooltip.content tooltipContentWithForecast ]
            Recharts.line [
                line.dot false
                line.dataKey (fun (p: TipBurnData.GraphNodeDto) -> p.AirTemperature)
                line.name "Luft Temperatur [°C]"
                line.stroke "#3182BD"
            ]

            Recharts.line [
                line.dot false
                line.dataKey (fun (p: TipBurnData.GraphNodeDto) -> p.SoilTemperature)
                line.name "Boden Temperatur [°C]"
                line.stroke "#FFC000"
            ]

            Recharts.cartesianGrid [ cartesianGrid.strokeDasharray [| 3; 3 |] ]

            match maybeForecastLine with
            | Some line -> Recharts.referenceLine line
            | None -> ()
        ]
    ]
    |> fullHeightGraphBox "Verlauf der Temperatur"

let wetnessGraph (maybeForecastLine: Option<IReferenceLineProperty list>) (graphData: TipBurnGraphData) =
    let wetnessTicks = AxisTicks.createYProperty [ 0; 1 ]

    let dataKey = (fun (p: TipBurnData.GraphNodeDto) -> p.Ticks) |> xAxis.dataKey

    Recharts.areaChart [
        areaChart.syncId "syncId1"
        areaChart.data graphData.Data
        areaChart.children [
            Recharts.xAxis [
                dataKey
                xAxis.number
                xAxis.scale.time
                xAxis.tickFormatter formatTimeTick
                xDomain
            ]
            Recharts.yAxis [
                yAxis.dataKey (fun (p: TipBurnData.GraphNodeDto) -> p.LeafletWetness)
                yAxis.number
                wetnessTicks
            ]
            Recharts.legend []
            Recharts.tooltip [ tooltip.content tooltipContentWithForecast ]
            Recharts.area [
                area.fillOpacity 0.2
                area.dataKey (fun (p: TipBurnData.GraphNodeDto) -> p.LeafletWetness)
                area.name "Blattnässe"
            ]

            match maybeForecastLine with
            | Some line -> Recharts.referenceLine line
            | None -> ()
        ]
    ]
    |> halfHeightGraphBox "Verlauf der Blattnässe"

let humidityGraph (maybeForecastLine: Option<IReferenceLineProperty list>) (graphData: TipBurnGraphData) =
    let humidityYAxis = AxisTicks.createYProperty graphData.HumidityAxis

    let dataKey = (fun (p: TipBurnData.GraphNodeDto) -> p.Ticks) |> xAxis.dataKey

    Recharts.lineChart [
        lineChart.syncId "syncId1"
        lineChart.data graphData.Data
        lineChart.children [
            Recharts.xAxis [
                dataKey
                xAxis.number
                xAxis.scale.time
                xAxis.tickFormatter formatTimeTick
                xDomain
            ]
            Recharts.yAxis [ yAxis.unit "%"; humidityYAxis ]
            Recharts.legend []
            Recharts.tooltip [ tooltip.content tooltipContentWithForecast ]
            Recharts.line [
                line.dot false
                line.dataKey (fun (p: TipBurnData.GraphNodeDto) -> p.AirHumidity)
                line.name "Luftfeuchtigkeit [%]"
                line.stroke "#3182BD"
            ]

            Recharts.line [
                line.dot false
                line.dataKey (fun (p: TipBurnData.GraphNodeDto) -> p.SoilMoisture)
                line.name "Bodenfeuchtigkeit [%]"
                line.stroke "#FFC000"
            ]

            Recharts.cartesianGrid [ cartesianGrid.strokeDasharray [| 3; 3 |] ]

            match maybeForecastLine with
            | Some line -> Recharts.referenceLine line
            | None -> ()
        ]
    ]
    |> fullHeightGraphBox "Verlauf der Feuchtigkeit"